import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Chip, Divider, Typography } from "@material-ui/core";
import {
  CallState,
  CorpSessionInfo,
  RewardsAccount,
  TokenizeCardErrors,
  TravelProductEnum,
} from "redmond";
import {
  B2BPaymentMethodSelectWorkflow,
  B2BSpinner,
  BannerSeverity,
  CheckoutPaymentForm,
  createElementId,
  GenericInfoPopup,
  getHSPEarnOfferDescription,
  Icon,
  IconName,
  LoadingIndicator,
  LoadingPopup,
  NotificationBanner,
  PoweredByHopper,
  TEST_CARD_LAST_FOURS,
  useDeviceTypes,
  usePrevious,
  paymentTextConstants,
  Tooltip,
  SanitizedHtml,
} from "halifax";
import clsx from "clsx";

import "./styles.scss";
import { RouteComponentProps } from "react-router";
import {
  isCaponeTenant,
  isVesperTenant,
  getConfigTenant,
} from "@capone/common";
import { RewardsSelection } from "../RewardsSelection";
import { PaymentCardConnectorProps } from "./container";
import { config } from "../../../../api/config";
import { VACATION_RENTALS_REWARDS_DISABLED_BANNER_TEXT } from "../../../rewards/components/RewardsBanner/constants";
import {
  ANNUAL_TRAVEL_CREDITS,
  AVAILABLE,
  C1_AUTO_SELECT_CARD_AT_CHECKOUT,
  CONTROL,
  CREDIT_OFFER_STACKING_V1,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  TRAVEL_SALE,
  TRAVEL_SALE_VARIANTS,
  useExperiments,
  VCN_ENABLEMENT,
} from "../../../../context/experiments";
import { TravelWalletSelection } from "../TravelWalletSelection";
import { fetchAllEarnForProduct } from "../../actions/actions";
import {
  PAYMENT_METHOD_SUBTITLE,
  PAYMENT_METHOD_SUBTITLE_AMOUNT,
} from "./textConstants";
import { ClientContext } from "../../../../App";
import { useExperimentIsVariant } from "@capone/experiments";
import { shouldAutoSelectCard } from "@capone/common/src/utils/shouldAutoSelectCard";

export interface IPaymentCardProps
  extends PaymentCardConnectorProps,
    RouteComponentProps {
  disabled?: boolean;
  className?: string;
  isXmodRedesign?: boolean;
}

export const shouldDisableCard =
  (canUseAllCards: boolean, isLifestyleCollection?: boolean) =>
  (rewardsAccount: RewardsAccount) => {
    if (canUseAllCards) {
      return false;
    }
    const allowedProducts = isCaponeTenant(config.TENANT)
      ? [
          ...(isLifestyleCollection ? ["Venture", "Spark Miles"] : []),
          "Venture X",
          "Spark Travel Elite",
          "Venture X Business",
        ]
      : ["Venture Business", "Spark Miles", "Spark Miles Select"];
    const isInDisplayName = (product: string) =>
      isCaponeTenant(config.TENANT)
        ? rewardsAccount.productDisplayName.includes(product) &&
          !rewardsAccount.productDisplayName.includes("VentureOne") &&
          !rewardsAccount.productDisplayName.includes("Select")
        : rewardsAccount.productDisplayName.includes(product);
    const isCardEnabled = allowedProducts.some(isInDisplayName);
    return !isCardEnabled;
  };

export const PaymentCard = ({
  listPaymentMethods,
  verifyPaymentMethod,
  deletePaymentMethod,
  setSelectedPaymentMethodId,
  fetchRewardsAccounts,
  fetchProductToEarn,
  selectedRewardsPaymentAccount,
  selectedRewardsPaymentAccountId,
  isCreditCardPaymentRequired,
  totalCreditCardPaymentRequired,
  paymentMethods,
  rewardsAccounts,
  selectedPaymentMethodId,
  listPaymentMethodCallState,
  verifyPaymentMethodCallState,
  deletePaymentMethodCallState,
  disabled = false,
  hasError,
  className,
  earnValuesByRewardAcctId,
  largestValueAccount,
  isTravelCreditPaymentOnly,
  isStackedTravelWalletPaymentOnly,
  isTravelWalletOfferPaymentOnly,
  canRedeemRewards,
  offerToApply,
  isXmodRedesign,
  credits,
  filteredOffers,
  walletLoaded,
}: IPaymentCardProps) => {
  const { matchesMobile } = useDeviceTypes();
  const { sessionInfo, isRewardsDisabled } = useContext(ClientContext);
  const isVesper = isVesperTenant(getConfigTenant());
  const [openErrorPaymentModal, setOpenErrorPaymentModal] = useState(false);
  const [isNotCapOneAccount, setIsNotCapOneAccount] = useState(false);
  const [tokenizeErrors, setTokenizeErrors] = useState<TokenizeCardErrors[]>(
    [],
  );

  const allowAnyCards = useExperimentIsVariant(
    "c1-corporate-credit-card-validation",
    "any-spark",
  );

  const corpSessionInfo = sessionInfo && (sessionInfo as CorpSessionInfo);
  const isNonFinancialUser = corpSessionInfo?.corporateInfo?.cap1Role === "NFU";

  useEffect(() => {
    listPaymentMethods();
    fetchRewardsAccounts(true, sessionInfo, true);
  }, []);

  const expState = useExperiments();

  const autoSelectExperimentOn =
    getExperimentVariant(
      expState.experiments,
      C1_AUTO_SELECT_CARD_AT_CHECKOUT,
    ) === AVAILABLE;

  useEffect(() => {
    const toSelect = shouldAutoSelectCard({
      paymentMethods,
      filteredOffers,
      credits,
      selectedPaymentMethodId,
      experimentOn: autoSelectExperimentOn,
      walletLoaded,
    });
    if (!toSelect) return;
    const card = paymentMethods[0];
    const matchingRewardsAccount = rewardsAccounts.find(
      (account) =>
        account.lastFour === card.last4 ||
        (card.last4 &&
          account.lastFourVirtualCardNumbers?.includes(card.last4)),
    );
    setSelectedPaymentMethodId({
      paymentMethodId: toSelect,
      accountId: matchingRewardsAccount?.accountReferenceId,
    });
  }, [
    paymentMethods,
    filteredOffers,
    credits,
    autoSelectExperimentOn,
    walletLoaded,
    rewardsAccounts,
    setSelectedPaymentMethodId,
  ]);

  const ineligibleRewardsAccounts = useMemo(
    () =>
      rewardsAccounts.filter(
        (account) => !(account.allowRewardsRedemption ?? true),
      ),
    [rewardsAccounts],
  );

  const handleOnAddPaymentMethod = (token: string, last4: string) => {
    // note: cap1 specific logic:
    // A card should be deemed ineligible if the last four digits do not match one of the cards associated with their accounts.
    const account = rewardsAccounts.find(
      (account) =>
        account.lastFour === last4 ||
        account.lastFourVirtualCardNumbers?.includes(last4) ||
        account.discoverNetworkFlipInfo?.transferFromCardNumberLastFour ===
          last4,
    );
    let matchingRewardsAccount = account;

    const isAddingVCNPaymentMethod = !!(
      matchingRewardsAccount?.lastFourVirtualCardNumbers &&
      matchingRewardsAccount?.lastFourVirtualCardNumbers?.includes(last4)
    );

    // TODO: bad practice, remove this in favor of real test accounts when we have them
    const isTestCard =
      window.__mclean_env__.ENV !== "production" &&
      TEST_CARD_LAST_FOURS.includes(last4);
    if (isTestCard) {
      matchingRewardsAccount = rewardsAccounts[0];
    }

    if (!!matchingRewardsAccount || isTestCard) {
      verifyPaymentMethod(
        { token },
        matchingRewardsAccount?.accountReferenceId!,
        isAddingVCNPaymentMethod,
      );
    } else {
      setIsNotCapOneAccount(true);
      setOpenErrorPaymentModal(true);
    }
  };

  const handleCloseErrorPopup = () => {
    isNotCapOneAccount && setIsNotCapOneAccount(false);
    setOpenErrorPaymentModal(false);
  };

  const PaymentAddingElement = () => (
    <LoadingPopup
      indicatorSize="small"
      indicator={B2BSpinner}
      open
      popupSize="small"
      message="Adding your payment method"
      footer={PoweredByHopper}
    />
  );

  const renderCheckoutPaymentForm = (isOpen: boolean) => (
    <CheckoutPaymentForm
      loading={verifyPaymentMethodCallState === CallState.InProcess}
      loadingEl={<PaymentAddingElement />}
      onSubmit={(token, last4) => {
        handleOnAddPaymentMethod(token, last4);
      }}
      saveLabel="Save"
      onError={(errors) => {
        setTokenizeErrors(errors);
        setOpenErrorPaymentModal(true);
      }}
      spreedlyEnvironmentKey={config.spreedlyEnvironmentKey}
      isMobile={matchesMobile}
      className="b2b"
      tenant={config.TENANT}
      prefillData={null}
      isOpen={isOpen}
    />
  );

  const vcnEnablement = getExperimentVariant(
    expState.experiments,
    VCN_ENABLEMENT,
  );
  const isVCNEnabled = useMemo(
    () => vcnEnablement === AVAILABLE,
    [vcnEnablement],
  );

  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1,
  );
  const isCreditAndOfferStackingExperimentV1 = useMemo(
    () => creditAndOfferStackingExperimentV1 === AVAILABLE,
    [creditAndOfferStackingExperimentV1],
  );

  const isAnnualTravelCreditsExperiment =
    getExperimentVariant(expState.experiments, ANNUAL_TRAVEL_CREDITS) ===
    AVAILABLE;

  const travelSaleVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS,
  );
  const isTravelSaleEnabled = travelSaleVariant !== CONTROL;

  const showEarnEnhancement =
    !!largestValueAccount && !!largestValueAccount.earn.homesMultiplier;

  const rewardsSelectedNeeded =
    canRedeemRewards || isCaponeTenant(config.TENANT)
      ? selectedRewardsPaymentAccountId === undefined
      : false;

  const previousOfferToApply = usePrevious(offerToApply);

  useEffect(() => {
    const offer = offerToApply || previousOfferToApply;

    const hspEarnDescription = getHSPEarnOfferDescription(offer);

    // re-fetch product to earn when toggling HSP earn offer to get correct earn
    if (hspEarnDescription) {
      fetchAllEarnForProduct();
    }
  }, [offerToApply]);

  const isPaymentMethodSelectDisabled =
    selectedRewardsPaymentAccount?.isTiered ||
    !isCreditCardPaymentRequired ||
    !totalCreditCardPaymentRequired ||
    rewardsSelectedNeeded;

  const paymentMethodWorkflowTitles = () => ({
    addPaymentCta:
      rewardsAccounts.length === 1 ? (
        <Box
          className={clsx(
            "add-payment-cta-container",
            {
              mobile: matchesMobile,
              disabled,
            },
            className,
          )}
        >
          <Typography variant="body1">
            {paymentTextConstants.CTA_SINGLE_ACCOUNT_ADD_YOUR_TEXT}
            <b className="card-name">{rewardsAccounts[0].productDisplayName}</b>
            {paymentTextConstants.CTA_SINGLE_ACCOUNT_CREDIT_CARD_TEXT}
          </Typography>
          {rewardsAccounts[0].earn.homesMultiplier &&
          rewardsAccounts[0].earn.homesMultiplier > 0 ? (
            <Chip
              label={`Earn ${rewardsAccounts[0].earn.homesMultiplier}X on vacation rentals`}
              className={clsx("earn-chip", {
                enabled: !isPaymentMethodSelectDisabled,
              })}
            />
          ) : null}
        </Box>
      ) : (
        paymentTextConstants.ADD_PAYMENT_METHOD_CTA_MULTIPLE_ACCOUNTS
      ),
    addPaymentModalTitle: paymentTextConstants.ADD_PAYMENT_METHOD_MODAL_TITLE,
    addAdditionalPaymentCta:
      paymentTextConstants.ADD_ADDITIONAL_PAYMENT_METHOD_CTA,
    backToCardSelectionCta: paymentTextConstants.BACK_TO_CARD_SELECTION_CTA,
    paymentFormHeader: (cardName: string) => (
      <SanitizedHtml
        as={Typography}
        variant="h4"
        html={paymentTextConstants.ADD_PAYMENT_FORM_HEADER_TEXT(cardName)}
      />
    ),
    cardEndingIn: paymentTextConstants.CARD_ENDING_IN_TEXT,
    addPaymentBannerText: undefined,
  });

  const getParadiseCardMessage = () =>
    rewardsAccounts.find((acct) =>
      acct.productDisplayName.toLowerCase().includes("paradise"),
    ) ? (
      <NotificationBanner
        className={clsx("paradise-banner-notification", {
          mobile: matchesMobile,
        })}
        label="Bookings made with your Paradise card through Capital One Travel cannot be serviced by concierge services. If you’d like concierge to manage your booking, book in the Capital One concierge app."
        severity={BannerSeverity.NOTICE}
      />
    ) : null;

  const rewardsTitleId = createElementId("rewardsTitle");
  const rewardsSubtitleId = createElementId("rewardsSubtitle");

  return (
    <>
      {listPaymentMethodCallState === CallState.InProcess ||
      deletePaymentMethodCallState === CallState.InProcess ? (
        <LoadingIndicator
          indicatorSize="small"
          indicator={B2BSpinner}
          message={
            listPaymentMethodCallState === CallState.InProcess
              ? `Fetching`
              : `Deleting`
          }
        />
      ) : (
        <Box
          className={clsx("vacation-rentals-payment-methods-container", {
            mobile: matchesMobile,
            disabled,
            xmod: isXmodRedesign,
            redesigned: isVesper,
          })}
        >
          <Typography className="step-title" variant="h2">
            {matchesMobile
              ? paymentTextConstants.MOBILE_PAYMENT_STEP_TITLE(
                  canRedeemRewards || isCaponeTenant(config.TENANT),
                )
              : paymentTextConstants.PAYMENT_STEP_TITLE(
                  2,
                  canRedeemRewards || isCaponeTenant(config.TENANT),
                )}
          </Typography>
          <Box className="payment-step-subtitle-wrapper">
            <Typography
              variant="body2"
              className={clsx("payment-step-subtitle-text", {
                vesper: isVesper,
              })}
            >
              {isCreditAndOfferStackingExperimentV1 && !isNonFinancialUser
                ? paymentTextConstants.PAYMENT_CARD_SUBTITLE_WITH_CREDITS_AND_OFFERS
                : matchesMobile
                ? paymentTextConstants.MOBILE_PAYMENT_STEP_SUBTITLE(
                    !isNonFinancialUser &&
                      (canRedeemRewards || isCaponeTenant(config.TENANT)),
                  )
                : paymentTextConstants.PAYMENT_STEP_SUBTITLE(
                    !isNonFinancialUser &&
                      (canRedeemRewards || isCaponeTenant(config.TENANT)),
                  )}
            </Typography>
            {!isNonFinancialUser && (
              <Tooltip
                content={
                  <Typography className="payment-earn-info-popover-content">
                    {paymentTextConstants.PAYMENT_EARN_INFO_TOOLTIP_TEXT}
                  </Typography>
                }
                triggerClassName="payment-earn-info-btn"
                title="Rewards & Payment"
              >
                <Icon name={IconName.InfoCircle} />
              </Tooltip>
            )}
          </Box>
          {canRedeemRewards && (
            <>
              {ineligibleRewardsAccounts.length > 0 && (
                <NotificationBanner
                  className="authorized-users-rewards-banner"
                  label={paymentTextConstants.INELIGIBLE_ACCOUNTS_NOTICE(
                    rewardsAccounts,
                  )}
                  severity={BannerSeverity.NOTICE}
                  tooltip={
                    rewardsAccounts.length === ineligibleRewardsAccounts.length
                      ? {
                          label:
                            paymentTextConstants.INELIGIBLE_ACCOUNTS_TOOLTIP(
                              ineligibleRewardsAccounts,
                            ),
                          icon: IconName.InfoCircle,
                        }
                      : undefined
                  }
                />
              )}
              <Divider className="payment-methods-container-divider" />
              {isCreditAndOfferStackingExperimentV1 && (
                <TravelWalletSelection
                  disabled={disabled}
                  isMobile={matchesMobile}
                  isAnnualTravelCreditsExperiment={
                    isAnnualTravelCreditsExperiment
                  }
                  isCreditAndOfferStackingExperimentV1={
                    isCreditAndOfferStackingExperimentV1
                  }
                  isTravelSale={isTravelSaleEnabled}
                />
              )}{" "}
              {getParadiseCardMessage()}
              <Typography
                component="h3"
                variant="h4"
                className="rewards-account-title"
              >
                {paymentTextConstants.REWARDS_ACCOUNT_TITLE(config.TENANT)}
              </Typography>
              <SanitizedHtml
                as={Typography}
                variant="body2"
                html={paymentTextConstants.REWARDS_ACCOUNT_SUBTITLE(
                  config.TENANT,
                )}
                className="rewards-accounts-subtitle"
              />
              {isRewardsDisabled && (
                <NotificationBanner
                  className="rewards-disabled-banner"
                  label={VACATION_RENTALS_REWARDS_DISABLED_BANNER_TEXT}
                  severity={BannerSeverity.WARNING}
                  icon={
                    <Icon
                      className="banner-icon"
                      name={IconName.WarningAlert}
                    />
                  }
                />
              )}
              <RewardsSelection
                {...{
                  disabled:
                    disabled ||
                    isRewardsDisabled ||
                    (isCreditAndOfferStackingExperimentV1 &&
                      (isTravelCreditPaymentOnly ||
                        isStackedTravelWalletPaymentOnly ||
                        isTravelWalletOfferPaymentOnly)),
                  rewardsTitleId,
                  rewardsSubtitleId,
                }}
              />
            </>
          )}
          <Divider className="payment-methods-container-divider" />
          <Typography
            className="payment-method-title"
            component="h3"
            variant="h4"
          >
            {paymentTextConstants.PAYMENT_METHOD_TITLE}
          </Typography>
          {!isPaymentMethodSelectDisabled && (
            <Typography variant="body2" className="payment-method-subtitle">
              {!selectedPaymentMethodId && PAYMENT_METHOD_SUBTITLE}
              <b>
                {PAYMENT_METHOD_SUBTITLE_AMOUNT(totalCreditCardPaymentRequired)}
              </b>
            </Typography>
          )}
          <B2BPaymentMethodSelectWorkflow
            allowAnyCards={allowAnyCards || isNonFinancialUser}
            errorModalOpen={openErrorPaymentModal}
            rewardsAccounts={rewardsAccounts ?? []}
            savedPayments={paymentMethods ?? []}
            selectedPaymentHopperId={selectedPaymentMethodId}
            disabled={
              isPaymentMethodSelectDisabled ||
              disabled ||
              hasError ||
              (isCreditAndOfferStackingExperimentV1 &&
                (isTravelCreditPaymentOnly ||
                  isStackedTravelWalletPaymentOnly ||
                  isTravelWalletOfferPaymentOnly))
            }
            selectPaymentMethod={(paymentId, rewardsAccount) => {
              setSelectedPaymentMethodId({
                paymentMethodId: paymentId,
                accountId: rewardsAccount?.accountReferenceId,
              });
              fetchProductToEarn();
            }}
            removePaymentMethod={(paymentId: string) => {
              deletePaymentMethod({ paymentId });
            }}
            titles={paymentMethodWorkflowTitles()}
            renderCheckoutPaymentForm={renderCheckoutPaymentForm}
            isMobile={matchesMobile}
            product={TravelProductEnum.Homes}
            loading={verifyPaymentMethodCallState === CallState.InProcess}
            buttonClassName="b2b"
            fullScreenWithBanner={matchesMobile}
            paymentMethodDisabled={() => false}
            earnValuesByRewardAcctId={
              showEarnEnhancement ? earnValuesByRewardAcctId : undefined
            }
            showEarnValue={showEarnEnhancement}
            isVCNEnabled={isVCNEnabled}
            tenant={config.TENANT}
          />
        </Box>
      )}
      <GenericInfoPopup
        open={openErrorPaymentModal}
        image={
          <Icon
            className="error-icon"
            name={
              isNotCapOneAccount
                ? IconName.ErrorState
                : IconName.UnableToProcess
            }
          />
        }
        title={
          isNotCapOneAccount
            ? paymentTextConstants.CAP_ONE_INVALID_CREDIT_CARD_TITLE
            : paymentTextConstants.UNABLED_TO_ADD_PAYMENT
        }
        subtitle={
          isNotCapOneAccount
            ? paymentTextConstants.CAP_ONE_INVALID_CREDIT_CARD_SUBTITLE(
                config.TENANT,
              )
            : tokenizeErrors.length > 0
            ? tokenizeErrors[0].message
            : paymentTextConstants.ADD_PAYMENT_AGAIN
        }
        buttons={[
          {
            buttonText: paymentTextConstants.EDIT_PAYMENT_METHOD,
            onClick: () => {
              handleCloseErrorPopup();
            },
            defaultStyle: "h4r-secondary",
          },
          {
            buttonText: paymentTextConstants.TRY_AGAIN,
            onClick: () => {
              handleCloseErrorPopup();
            },
            defaultStyle: "h4r-primary",
            buttonWrapperClassName: "b2b",
          },
        ]}
        isMobile={matchesMobile}
      />
    </>
  );
};
